import {
  type HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import { useEffect, useRef } from "react";
import type { ApiClient } from "@/api/client";
import { setLiveConnection } from "@/api/liveConnection";
import { useApi } from "@/api/useApi";

/** Where one receipt has got to on the device adding it. */
export type ReceiptProgress = {
  reportId: string;
  /** Unique to the upload: the page that's adding it and its place in that page's queue. */
  key: string;
  name: string;
  status: "waiting" | "uploading" | "reading" | "done" | "failed";
  progress: number;
  device: "phone" | "computer";
  error?: string | null;
};

type Listener = {
  onProgress: (progress: ReceiptProgress) => void;
  /** A report changed, or "*" when updates may have been missed and everything should reload. */
  onChanged: (reportId: string) => void;
};

/*
 * One connection per page to the API's expenses hub, which keeps a person's devices in step: a
 * receipt snapped on the phone shows on the computer as it's read, and any change to a report
 * (an expense added, edited or removed) shows on the other devices with it open.
 * Nothing depends on it: without it, a report shows the change when it's next opened.
 */
const listeners = new Set<Listener>();
let connection: HubConnection | undefined;
let client: ApiClient | undefined;
let starting: Promise<void> | undefined;
let sending: Promise<void> = Promise.resolve();

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

function connect(api: ApiClient): HubConnection {
  client = api;
  if (connection) return connection;
  const built = new HubConnectionBuilder()
    .withUrl(`${api.baseUrl}/hubs/expenses`, {
      accessTokenFactory: () => (client ? client.token() : ""),
    })
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
    .configureLogging(LogLevel.None)
    .build();
  built.on("ReceiptProgress", (progress: ReceiptProgress) =>
    listeners.forEach((listener) => listener.onProgress(progress)),
  );
  built.on("ReportChanged", ({ reportId }: { reportId: string }) =>
    listeners.forEach((listener) => listener.onChanged(reportId)),
  );
  built.onreconnected(() => listeners.forEach((listener) => listener.onChanged("*")));
  setLiveConnection(() => built.connectionId);
  connection = built;
  return built;
}

function start(hub: HubConnection): Promise<void> {
  starting ??= hub.start().finally(() => {
    starting = undefined;
  });
  return starting;
}

/** Waits a few seconds for the connection, as on a phone just back from its camera app. */
async function ready(hub: HubConnection): Promise<boolean> {
  for (let tries = 0; tries < 10; tries++) {
    if (hub.state === HubConnectionState.Connected) return true;
    if (hub.state === HubConnectionState.Disconnected) await start(hub).catch(() => wait(1000));
    else await wait(500);
  }
  return hub.state === HubConnectionState.Connected;
}

/** Tells the person's other devices where a receipt has got to, in the order it happened. */
export function sendProgress(progress: ReceiptProgress): void {
  const hub = connection;
  if (!hub) return;
  sending = sending
    .then(async () => {
      if (await ready(hub)) await hub.send("ReceiptProgress", progress);
    })
    .catch(() => undefined);
}

/** Receipts being added to this report on the person's other devices, and the report changing. */
export function useLiveReport(
  reportId: string,
  {
    onProgress,
    onChanged,
  }: { onProgress?: (progress: ReceiptProgress) => void; onChanged?: () => void },
): void {
  const api = useApi();
  const latest = useRef({ reportId, onProgress, onChanged });
  useEffect(() => {
    latest.current = { reportId, onProgress, onChanged };
  });

  useEffect(() => {
    const hub = connect(api);
    const listener: Listener = {
      onProgress: (progress) => {
        if (progress.reportId === latest.current.reportId) latest.current.onProgress?.(progress);
      },
      onChanged: (changed) => {
        if (changed === "*" || changed === latest.current.reportId) latest.current.onChanged?.();
      },
    };
    listeners.add(listener);
    if (hub.state === HubConnectionState.Disconnected) void start(hub).catch(() => undefined);

    // A phone back from another app may have dropped the connection, and missed what happened.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (hub.state === HubConnectionState.Disconnected) {
        void start(hub)
          .then(() => listener.onChanged("*"))
          .catch(() => undefined);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      listeners.delete(listener);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [api]);
}
